import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  Plus,
  ArrowLeft,
  Pencil,
  Trash2,
  Clock,
  Boxes,
  ListTree,
  TrendingUp,
  Calendar,
  Users,
  Factory,
  CheckCircle2,
  AlertTriangle,
  Hammer,
  Timer
} from "lucide-react";
import { usePermissions } from "../../hooks/usePermissions";
import { handleApiError } from "../../utils/errorHandler";
import { useTranslation } from "../../contexts/I18nContext";

const STATI_COMMESSA = {
  aperta: { label: "Aperta", color: "bg-blue-100 text-blue-800" },
  in_corso: { label: "In Corso", color: "bg-yellow-100 text-yellow-800" },
  sospesa: { label: "Sospesa", color: "bg-orange-100 text-orange-800" },
  completata: { label: "Completata", color: "bg-green-100 text-green-800" },
  annullata: { label: "Annullata", color: "bg-red-100 text-red-800" }
};

// Renderer ricorsivo dell'albero sottocommesse
function renderSottocommesseTree(allSottocomm, parentId, level, onAddChild, onDelete) {
  const children = allSottocomm.filter(sc =>
    (sc.parent_sottocommessa_id || null) === (parentId || null)
  );
  const rows = [];
  children.forEach((sc) => {
    rows.push(
      <TableRow key={sc.id} data-testid={`sottocommessa-row-${sc.id}`}>
        <TableCell className="font-mono">
          <span style={{ paddingLeft: `${level * 20}px` }} className="inline-flex items-center gap-2">
            {level > 0 && <span className="text-muted-foreground">└</span>}
            {sc.codice}
          </span>
        </TableCell>
        <TableCell>{sc.titolo}</TableCell>
        <TableCell>
          {sc.tipo_attivita_nome ? (
            <Badge variant="outline" className="text-xs">{sc.tipo_attivita_nome}</Badge>
          ) : (
            <span className="text-muted-foreground text-xs">-</span>
          )}
        </TableCell>
        <TableCell className="text-right">{sc.ore_budget || 0}h</TableCell>
        <TableCell className="text-right">{sc.ore_effettive?.toFixed(1) || 0}h</TableCell>
        <TableCell className="text-right">
          <div className="flex items-center justify-end gap-2">
            <Progress value={sc.avanzamento_percentuale || 0} className="w-16 h-2" />
            <span className="text-sm">{sc.avanzamento_percentuale?.toFixed(0) || 0}%</span>
          </div>
        </TableCell>
        <TableCell>
          <div className="flex gap-1">
            <Button variant="ghost" size="icon" title="Aggiungi sottocommessa figlia" onClick={() => onAddChild(sc.id)} data-testid={`btn-add-child-${sc.id}`}>
              <Plus className="h-4 w-4 text-blue-500" />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => onDelete(sc.id)} data-testid={`btn-delete-${sc.id}`}>
              <Trash2 className="h-4 w-4 text-red-500" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    );
    // ricorsione sui figli
    rows.push(...renderSottocommesseTree(allSottocomm, sc.id, level + 1, onAddChild, onDelete));
  });
  return rows;
}

export default function ProduzioneCommessaDetailPage() {
  const { t } = useTranslation();
  const { tenant, commessaId } = useParams();
  const navigate = useNavigate();
  const [commessa, setCommessa] = useState(null);
  const [sottocommesse, setSottocommesse] = useState([]);
  const [fasi, setFasi] = useState([]);
  const [materiale, setMateriale] = useState([]);
  const [avanzamenti, setAvanzamenti] = useState([]);
  const [ore, setOre] = useState([]);
  const [tipiLavorazione, setTipiLavorazione] = useState([]);
  const [tipiAttivita, setTipiAttivita] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showSottocommessaDialog, setShowSottocommessaDialog] = useState(false);
  const [showFaseDialog, setShowFaseDialog] = useState(false);
  const [showAvanzamentoDialog, setShowAvanzamentoDialog] = useState(false);
  const [showOreDialog, setShowOreDialog] = useState(false);
  
  const [sottocommessaForm, setSottocommessaForm] = useState({ codice: "", titolo: "", descrizione: "", ore_budget: 0, ordine: 0, parent_sottocommessa_id: "__none__", tipo_attivita_id: "__none__", data_inizio: "", data_fine: "" });
  const [faseForm, setFaseForm] = useState({ codice: "", nome: "", descrizione: "", ore_budget: 0, ordine: 0, sottocommessa_id: "__main__" });
  const [avanzamentoForm, setAvanzamentoForm] = useState({ data_avanzamento: "", percentuale_avanzamento: 0, ore_consuntivo: 0, note: "", sottocommessa_id: "__main__" });
  const [oreForm, setOreForm] = useState({ data: "", ore: 0, tipo_lavorazione_id: "", operatore: "", note: "", sottocommessa_id: "__main__" });

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [commessaRes, sottoRes, fasiRes, matRes, avanzRes, oreRes, tipiRes, tipiAttRes] = await Promise.all([
        api.get(`/tenant/produzione/commesse/${commessaId}`),
        api.get(`/tenant/produzione/commesse/${commessaId}/sottocommesse`),
        api.get(`/tenant/produzione/commesse/${commessaId}/fasi`),
        api.get(`/tenant/produzione/materiale?commessa_id=${commessaId}`),
        api.get(`/tenant/produzione/avanzamenti?commessa_id=${commessaId}`),
        api.get(`/tenant/produzione/ore?commessa_id=${commessaId}`),
        api.get("/tenant/produzione/tipi-lavorazione"),
        api.get("/tenant/produzione/tipi-attivita")
      ]);
      setCommessa(commessaRes.data);
      setSottocommesse(sottoRes.data);
      setFasi(fasiRes.data);
      setMateriale(matRes.data);
      setAvanzamenti(avanzRes.data);
      setOre(oreRes.data);
      setTipiLavorazione(tipiRes.data);
      setTipiAttivita(tipiAttRes.data);
    } catch (error) {
      
      handleApiError(error, t("errors.loadFailed"));
      navigate(`/${tenant}/produzione/commesse`);
    } finally {
      setLoading(false);
    }
  }, [commessaId, t, navigate, tenant]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Handlers per sottocommesse
  const openCreateSottocommessa = (parentId = null) => {
    setSottocommessaForm({
      codice: "", titolo: "", descrizione: "", ore_budget: 0, ordine: 0,
      parent_sottocommessa_id: parentId || "__none__",
      tipo_attivita_id: "__none__",
      data_inizio: "", data_fine: "",
    });
    setShowSottocommessaDialog(true);
  };

  const handleSaveSottocommessa = async () => {
    if (!sottocommessaForm.codice || !sottocommessaForm.titolo) {
      toast.error("Codice e titolo obbligatori");
      return;
    }
    try {
      const payload = {
        ...sottocommessaForm,
        commessa_id: commessaId,
        parent_sottocommessa_id: sottocommessaForm.parent_sottocommessa_id === "__none__" ? null : sottocommessaForm.parent_sottocommessa_id,
        tipo_attivita_id: sottocommessaForm.tipo_attivita_id === "__none__" ? null : sottocommessaForm.tipo_attivita_id,
      };
      await api.post("/tenant/produzione/sottocommesse", payload);
      toast.success("Sottocommessa creata");
      setShowSottocommessaDialog(false);
      setSottocommessaForm({ codice: "", titolo: "", descrizione: "", ore_budget: 0, ordine: 0, parent_sottocommessa_id: "__none__", tipo_attivita_id: "__none__", data_inizio: "", data_fine: "" });
      loadData();
    } catch (error) {
      handleApiError(error, t("errors.saveFailed"));
    }
  };

  const handleDeleteSottocommessa = async (id) => {
    if (!confirm(t("confirm.deleteSubOrder"))) return;
    try {
      await api.delete(`/tenant/produzione/sottocommesse/${id}`);
      toast.success("Sottocommessa eliminata");
      loadData();
    } catch (error) {
      handleApiError(error, t("errors.deleteFailed"));
    }
  };

  // Handlers per fasi
  const handleSaveFase = async () => {
    if (!faseForm.codice || !faseForm.nome) {
      toast.error("Codice e nome obbligatori");
      return;
    }
    try {
      await api.post("/tenant/produzione/fasi", { 
        ...faseForm, 
        commessa_id: commessaId,
        sottocommessa_id: faseForm.sottocommessa_id === "__main__" ? null : faseForm.sottocommessa_id
      });
      toast.success("Fase creata");
      setShowFaseDialog(false);
      setFaseForm({ codice: "", nome: "", descrizione: "", ore_budget: 0, ordine: 0, sottocommessa_id: "__main__" });
      loadData();
    } catch (error) {
      handleApiError(error, t("errors.saveFailed"));
    }
  };

  // Handlers per avanzamento SAL
  const handleSaveAvanzamento = async () => {
    if (!avanzamentoForm.data_avanzamento || avanzamentoForm.percentuale_avanzamento < 0) {
      toast.error("Data e percentuale obbligatorie");
      return;
    }
    try {
      await api.post("/tenant/produzione/avanzamenti", { 
        ...avanzamentoForm, 
        commessa_id: commessaId,
        sottocommessa_id: avanzamentoForm.sottocommessa_id === "__main__" ? null : avanzamentoForm.sottocommessa_id
      });
      toast.success("Avanzamento registrato");
      setShowAvanzamentoDialog(false);
      setAvanzamentoForm({ data_avanzamento: "", percentuale_avanzamento: 0, ore_consuntivo: 0, note: "", sottocommessa_id: "__main__" });
      loadData();
    } catch (error) {
      handleApiError(error, t("errors.saveFailed"));
    }
  };

  // Handlers per ore
  const handleSaveOre = async () => {
    if (!oreForm.data || !oreForm.ore || !oreForm.tipo_lavorazione_id) {
      toast.error("Data, ore e tipo lavorazione obbligatori");
      return;
    }
    try {
      await api.post("/tenant/produzione/ore", { 
        ...oreForm, 
        commessa_id: commessaId,
        sottocommessa_id: oreForm.sottocommessa_id === "__main__" ? null : oreForm.sottocommessa_id
      });
      toast.success("Ore registrate");
      setShowOreDialog(false);
      setOreForm({ data: "", ore: 0, tipo_lavorazione_id: "", operatore: "", note: "", sottocommessa_id: "__main__" });
      loadData();
    } catch (error) {
      handleApiError(error, t("errors.saveFailed"));
    }
  };

  const handleDeleteOre = async (id) => {
    if (!confirm(t("confirm.deleteRegistration"))) return;
    try {
      await api.delete(`/tenant/produzione/ore/${id}`);
      toast.success("Registrazione eliminata");
      loadData();
    } catch (error) {
      handleApiError(error, t("errors.deleteFailed"));
    }
  };

  if (loading || !commessa) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const statoInfo = STATI_COMMESSA[commessa.stato] || STATI_COMMESSA.aperta;
  const totaleOre = ore.reduce((sum, o) => sum + (parseFloat(o.ore) || 0), 0);
  const ultimoAvanzamento = avanzamenti.length > 0 ? avanzamenti[0] : null;

  return (
    <div className="space-y-6" data-testid="commessa-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/${tenant}/produzione/commesse`)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold font-mono">{commessa.codice}</h1>
              <Badge className={statoInfo.color}>{statoInfo.label}</Badge>
            </div>
            <p className="text-lg text-muted-foreground">{commessa.titolo}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowOreDialog(true)}>
            <Timer className="h-4 w-4 mr-2" />
            Registra Ore
          </Button>
          <Button onClick={() => setShowAvanzamentoDialog(true)}>
            <TrendingUp className="h-4 w-4 mr-2" />
            Registra SAL
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Avanzamento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{ultimoAvanzamento?.percentuale_avanzamento?.toFixed(0) || 0}%</div>
            <Progress value={ultimoAvanzamento?.percentuale_avanzamento || 0} className="h-2 mt-2" />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Ore Lavorate</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{totaleOre.toFixed(1)}h</div>
            <p className="text-xs text-muted-foreground">Budget: {commessa.ore_budget_totali || 0}h</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Materiale</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">
              {materiale.filter(m => m.ricevuto).length}/{materiale.length}
            </div>
            <p className="text-xs text-muted-foreground">Ricevuti / Attesi</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Consegna</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-lg font-bold">
              {commessa.data_consegna_prevista 
                ? new Date(commessa.data_consegna_prevista).toLocaleDateString('it-IT')
                : "Non definita"}
            </div>
            {commessa.cliente_nome && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                <Users className="h-3 w-3" /> {commessa.cliente_nome}
              </p>
            )}
            {commessa.fornitore_ragione_sociale && (
              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                <Factory className="h-3 w-3" /> {commessa.fornitore_ragione_sociale}
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="sottocommesse" className="space-y-4">
        <TabsList>
          <TabsTrigger value="sottocommesse" className="flex items-center gap-2">
            <ListTree className="h-4 w-4" />
            Sottocommesse ({sottocommesse.length})
          </TabsTrigger>
          <TabsTrigger value="materiale" className="flex items-center gap-2">
            <Boxes className="h-4 w-4" />
            Materiale ({materiale.length})
          </TabsTrigger>
          <TabsTrigger value="ore" className="flex items-center gap-2">
            <Clock className="h-4 w-4" />
            Ore ({ore.length})
          </TabsTrigger>
          <TabsTrigger value="sal" className="flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            SAL ({avanzamenti.length})
          </TabsTrigger>
        </TabsList>

        {/* Sottocommesse Tab */}
        <TabsContent value="sottocommesse">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Sottocommesse</CardTitle>
              <Button size="sm" onClick={() => openCreateSottocommessa(null)} data-testid="btn-aggiungi-sottocommessa">
                <Plus className="h-4 w-4 mr-2" />
                Aggiungi
              </Button>
            </CardHeader>
            <CardContent>
              {sottocommesse.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">{t("production.noSubOrders")}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("common.code")}</TableHead>
                      <TableHead>Titolo</TableHead>
                      <TableHead>Tipo Attività</TableHead>
                      <TableHead className="text-right">Ore Budget</TableHead>
                      <TableHead className="text-right">Ore Effettive</TableHead>
                      <TableHead className="text-right">Avanzamento</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {renderSottocommesseTree(sottocommesse, null, 0, openCreateSottocommessa, handleDeleteSottocommessa)}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Materiale Tab */}
        <TabsContent value="materiale">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Materiale Atteso</CardTitle>
              <Button size="sm" onClick={() => navigate(`/${tenant}/produzione/materiale?commessa_id=${commessaId}`)}>
                <Plus className="h-4 w-4 mr-2" />
                Gestisci
              </Button>
            </CardHeader>
            <CardContent>
              {materiale.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">{t("production.noMaterial")}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("common.code")}</TableHead>
                      <TableHead>{t("common.description")}</TableHead>
                      <TableHead>Fornitore</TableHead>
                      <TableHead className="text-right">Quantita</TableHead>
                      <TableHead>{t("common.status")}</TableHead>
                      <TableHead>Posizione</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {materiale.map((mat) => (
                      <TableRow key={mat.id}>
                        <TableCell className="font-mono">{mat.codice_marca}</TableCell>
                        <TableCell>{mat.descrizione}</TableCell>
                        <TableCell>{mat.fornitore || "-"}</TableCell>
                        <TableCell className="text-right">
                          {mat.quantita_ricevuta || mat.quantita} {mat.unita_misura}
                        </TableCell>
                        <TableCell>
                          {mat.ricevuto ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle2 className="h-3 w-3 mr-1" />
                              Ricevuto
                            </Badge>
                          ) : (
                            <Badge variant="outline">In attesa</Badge>
                          )}
                        </TableCell>
                        <TableCell>{mat.area_nome || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ore Tab */}
        <TabsContent value="ore">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Registrazione Ore</CardTitle>
              <Button size="sm" onClick={() => setShowOreDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Registra
              </Button>
            </CardHeader>
            <CardContent>
              {ore.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">{t("production.noHours")}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("common.date")}</TableHead>
                      <TableHead>Tipo Lavorazione</TableHead>
                      <TableHead>Operatore</TableHead>
                      <TableHead>Sottocommessa</TableHead>
                      <TableHead className="text-right">Ore</TableHead>
                      <TableHead>{t("common.notes")}</TableHead>
                      <TableHead></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ore.map((o) => (
                      <TableRow key={o.id}>
                        <TableCell>{new Date(o.data).toLocaleDateString('it-IT')}</TableCell>
                        <TableCell>{o.tipo_lavorazione_nome || "-"}</TableCell>
                        <TableCell>{o.operatore || "-"}</TableCell>
                        <TableCell>{o.sottocommessa_codice || "-"}</TableCell>
                        <TableCell className="text-right font-medium">{o.ore}h</TableCell>
                        <TableCell className="max-w-[200px] truncate">{o.note || "-"}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="icon" onClick={() => handleDeleteOre(o.id)}>
                            <Trash2 className="h-4 w-4 text-red-500" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* SAL Tab */}
        <TabsContent value="sal">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>Stato Avanzamento Lavori (SAL)</CardTitle>
              <Button size="sm" onClick={() => setShowAvanzamentoDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Registra
              </Button>
            </CardHeader>
            <CardContent>
              {avanzamenti.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">{t("production.noProgress")}</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t("common.date")}</TableHead>
                      <TableHead>Sottocommessa</TableHead>
                      <TableHead className="text-right">Avanzamento</TableHead>
                      <TableHead className="text-right">Ore Consuntivo</TableHead>
                      <TableHead className="text-right">Efficienza</TableHead>
                      <TableHead>{t("common.notes")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {avanzamenti.map((av) => (
                      <TableRow key={av.id}>
                        <TableCell>{new Date(av.data_avanzamento).toLocaleDateString('it-IT')}</TableCell>
                        <TableCell>{av.sottocommessa_codice || "Commessa principale"}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Progress value={av.percentuale_avanzamento} className="w-16 h-2" />
                            <span className="font-medium">{av.percentuale_avanzamento}%</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">{av.ore_consuntivo || 0}h</TableCell>
                        <TableCell className="text-right">
                          {av.efficienza ? (
                            <Badge className={av.efficienza >= 100 ? "bg-green-100 text-green-800" : "bg-yellow-100 text-yellow-800"}>
                              {av.efficienza}%
                            </Badge>
                          ) : "-"}
                        </TableCell>
                        <TableCell className="max-w-[200px] truncate">{av.note || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Dialog Sottocommessa */}
      <Dialog open={showSottocommessaDialog} onOpenChange={setShowSottocommessaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("production.newSubOrder")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.code')} *</Label>
                <Input
                  value={sottocommessaForm.codice}
                  onChange={(e) => setSottocommessaForm({...sottocommessaForm, codice: e.target.value})}
                  placeholder="es. SC-001"
                  data-testid="input-codice-sotto"
                />
              </div>
              <div className="space-y-2">
                <Label>Ore Budget</Label>
                <Input
                  type="number"
                  value={sottocommessaForm.ore_budget}
                  onChange={(e) => setSottocommessaForm({...sottocommessaForm, ore_budget: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Titolo *</Label>
              <Input
                value={sottocommessaForm.titolo}
                onChange={(e) => setSottocommessaForm({...sottocommessaForm, titolo: e.target.value})}
                data-testid="input-titolo-sotto"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sottocommessa Padre</Label>
                <Select
                  value={sottocommessaForm.parent_sottocommessa_id}
                  onValueChange={(v) => setSottocommessaForm({...sottocommessaForm, parent_sottocommessa_id: v})}
                >
                  <SelectTrigger data-testid="select-parent-sotto">
                    <SelectValue placeholder="Nessuna (radice)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nessuna (radice)</SelectItem>
                    {sottocommesse.map(sc => (
                      <SelectItem key={sc.id} value={sc.id}>{sc.codice} - {sc.titolo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipologia Attività</Label>
                <Select
                  value={sottocommessaForm.tipo_attivita_id}
                  onValueChange={(v) => setSottocommessaForm({...sottocommessaForm, tipo_attivita_id: v})}
                >
                  <SelectTrigger data-testid="select-tipo-attivita">
                    <SelectValue placeholder="Nessuna" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">Nessuna</SelectItem>
                    {tipiAttivita.map(ta => (
                      <SelectItem key={ta.id} value={ta.id}>{ta.codice} - {ta.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("common.description")}</Label>
              <Textarea
                value={sottocommessaForm.descrizione}
                onChange={(e) => setSottocommessaForm({...sottocommessaForm, descrizione: e.target.value})}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data inizio (opz.)</Label>
                <Input type="date" value={sottocommessaForm.data_inizio || ""} onChange={(e) => setSottocommessaForm({...sottocommessaForm, data_inizio: e.target.value})} data-testid="input-data-inizio-sotto" />
              </div>
              <div className="space-y-2">
                <Label>Data fine (opz.)</Label>
                <Input type="date" value={sottocommessaForm.data_fine || ""} onChange={(e) => setSottocommessaForm({...sottocommessaForm, data_fine: e.target.value})} data-testid="input-data-fine-sotto" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Se non impostate, la sottocommessa eredita il periodo della commessa madre.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSottocommessaDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSaveSottocommessa} data-testid="btn-salva-sotto">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Avanzamento SAL */}
      <Dialog open={showAvanzamentoDialog} onOpenChange={setShowAvanzamentoDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("production.registerProgress")}</DialogTitle>
            <DialogDescription>Inserisci la percentuale di avanzamento lavori</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.date')} *</Label>
                <Input
                  type="date"
                  value={avanzamentoForm.data_avanzamento}
                  onChange={(e) => setAvanzamentoForm({...avanzamentoForm, data_avanzamento: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Percentuale Avanzamento *</Label>
                <div className="flex items-center gap-2">
                  <Input
                    type="number"
                    min="0"
                    max="100"
                    value={avanzamentoForm.percentuale_avanzamento}
                    onChange={(e) => setAvanzamentoForm({...avanzamentoForm, percentuale_avanzamento: parseFloat(e.target.value) || 0})}
                  />
                  <span>%</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Sottocommessa (opzionale)</Label>
                <Select 
                  value={avanzamentoForm.sottocommessa_id}
                  onValueChange={(v) => setAvanzamentoForm({...avanzamentoForm, sottocommessa_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("common.selectPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__main__">Commessa principale</SelectItem>
                    {sottocommesse.map(sc => (
                      <SelectItem key={sc.id} value={sc.id}>{sc.codice} - {sc.titolo}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Ore Consuntivo (fino a questa data)</Label>
                <Input
                  type="number"
                  value={avanzamentoForm.ore_consuntivo}
                  onChange={(e) => setAvanzamentoForm({...avanzamentoForm, ore_consuntivo: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea
                value={avanzamentoForm.note}
                onChange={(e) => setAvanzamentoForm({...avanzamentoForm, note: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAvanzamentoDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSaveAvanzamento}>Registra</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Ore */}
      <Dialog open={showOreDialog} onOpenChange={setShowOreDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("production.registerHours")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.date')} *</Label>
                <Input
                  type="date"
                  value={oreForm.data}
                  onChange={(e) => setOreForm({...oreForm, data: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Ore *</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  value={oreForm.ore}
                  onChange={(e) => setOreForm({...oreForm, ore: parseFloat(e.target.value) || 0})}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Tipo Lavorazione *</Label>
                <Select 
                  value={oreForm.tipo_lavorazione_id}
                  onValueChange={(v) => setOreForm({...oreForm, tipo_lavorazione_id: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t("common.selectPlaceholder")} />
                  </SelectTrigger>
                  <SelectContent>
                    {tipiLavorazione.map(t => (
                      <SelectItem key={t.id} value={t.id}>{t.codice} - {t.nome}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Operatore</Label>
                <Input
                  value={oreForm.operatore}
                  onChange={(e) => setOreForm({...oreForm, operatore: e.target.value})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Sottocommessa (opzionale)</Label>
              <Select 
                value={oreForm.sottocommessa_id}
                onValueChange={(v) => setOreForm({...oreForm, sottocommessa_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t("common.selectPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__main__">Commessa principale</SelectItem>
                  {sottocommesse.map(sc => (
                    <SelectItem key={sc.id} value={sc.id}>{sc.codice} - {sc.titolo}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea
                value={oreForm.note}
                onChange={(e) => setOreForm({...oreForm, note: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowOreDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSaveOre}>Registra</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
